import React, { useEffect, useState } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { getFirestore, doc, getDoc } from "firebase/firestore"; // Added Firestore imports
import LoadingSpinner from "../../AdminMetricDashboard/LoadingIndicator/LoadingSpinner";
import { getInteractionsDataByDay, createQuarterlyYearlyData } from "../../../../callable-cloud-functions/cloudFunctions";

function JobInteractionsByType({ daysSelected, setInteractionCount }) {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);

    const db = getFirestore();

    const formatToMonthName = (dateStr) => {
        // Replacing '-' with '/' forces JS to treat it as a local date 
        // instead of UTC, preventing the "day before" shift.
        const dateObj = new Date(dateStr.replace(/-/g, '\/')); 
        
        return dateObj.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
        });
    };

    function summarizeCounts(data) {
        let result = {
            bpo: { views_count: 0, apply_count: 0 },
            dh: { views_count: 0, apply_count: 0 }
        };
        data.forEach(day => {
            result.bpo.views_count += day.bpo?.view_count || 0;
            result.bpo.apply_count += day.bpo?.apply_count || 0;
            result.dh.views_count += day.dh?.view_count || 0;
            result.dh.apply_count += day.dh?.apply_count || 0;
        });
        return result;
    }

    // Helper to determine the docId for GlobalStats
    const getGlobalStatsDocId = (days) => {
        const today = new Date();
        if (days === 365) {
            return (today.getFullYear() - 1).toString(); // e.g., "2025"
        }
        if (days === 90) {
            const currentQuarter = Math.floor(today.getMonth() / 3);
            let targetQ, targetY;
            if (currentQuarter === 0) {
                targetQ = 4; targetY = today.getFullYear() - 1;
            } else {
                targetQ = currentQuarter; targetY = today.getFullYear();
            }
            return `Q${targetQ}_${targetY}`;
        }
        return null;
    };

    // Define a helper to get the label
    const getLabel = () => {
        if (daysSelected === 90) return "last quarter";
        if (daysSelected === 365) return "last year";
        return `last ${daysSelected} days`;
    };

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            let rawData = [];

            try {
                const docId = getGlobalStatsDocId(daysSelected);

                // LOGIC: If 90 or 365, check GlobalStats Collection
                if (docId) {
                    const docRef = doc(db, "GlobalStats", docId);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        // Based on 'docId' if its data present in "GlobalStats" collection
                        rawData = docSnap.data().data;
                    } else {
                        // If 'docId' didn't find in "GlobalStats" collection then we call 
                        // the callable function for creating that quarter or year data 
                        const periodType = daysSelected === 365 ? 'yearly' : 'quarterly';
                        await createQuarterlyYearlyData({ periodType });
                        
                        // Fetch the newly created doc
                        const newSnap = await getDoc(docRef);
                        rawData = newSnap.exists() ? newSnap.data().data : [];
                    }
                } else {
                    // Standard Logic for 3, 7, 14, 30 days
                    const { data } = await getInteractionsDataByDay(30);
                    rawData = data || [];
                }

                // Processing for UI
                const filtered = docId ? rawData : rawData.slice(-daysSelected);
                setInteractionCount(summarizeCounts(filtered));

                const processed = filtered.map((item) => ({
                    date: formatToMonthName(item.date),
                    bpo: item.bpo_count,
                    jobBoard: item.dh_count,
                    total: item.dh_count + item.bpo_count,
                }));

                setChartData(processed);
            } catch (error) {
                console.error("Error fetching interaction data:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [daysSelected]);

    return (
        <div className="mt-6 border border-gray-200 rounded-lg bg-white p-4">
            <div className="items-center w-full mb-4">
                <div className="flex w-full justify-between items-center">
                    <div>
                        <h2 className="text-2xl text-instant-teams-blue-Main font-bold text-foreground">
                            Interactions by Type <span className="text-sm">{`( ${getLabel()} )`}</span>
                        </h2>
                        <h2 className="text-lg text-gray-400">BPO Jobs, Job Board, and Total interactions over time</h2>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-60">
                    <LoadingSpinner />
                </div>
            ) : chartData.length === 0 ? (
                <div className="flex justify-center items-center h-60 text-gray-400">
                    No data available for this period.
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis 
                            dataKey="date" 
                            stroke="#64748b" 
                            fontSize={12} 
                            tickLine={false} 
                            axisLine={false}
                        />
                        <YAxis 
                            stroke="#64748b" 
                            fontSize={12} 
                            tickLine={false} 
                            axisLine={false} 
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: "white",
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                            }}
                        />
                        <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />

                        {/* Updated distinguishable colors */}
                        <Bar 
                            dataKey="bpo" 
                            fill="#008080" 
                            name="BPO Jobs" 
                            radius={[4, 4, 0, 0]} 
                            barSize={20}
                        />
                        <Bar 
                            dataKey="jobBoard" 
                            fill="#1F4A6B" 
                            name="Job Board" 
                            radius={[4, 4, 0, 0]} 
                            barSize={20}
                        />
                        <Bar 
                            dataKey="total" 
                            fill="#6366F1" 
                            name="Total Interactions" 
                            radius={[4, 4, 0, 0]} 
                            barSize={20}
                        />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}

export default JobInteractionsByType;